import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { bridgeCoachingNote } from "../../lib/bridgeCoachingNote";
import type { Deal } from "../../lib/types";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import MentionInput from "../coaching/MentionInput";
import {
  mentionUserIds,
  renderMessageBody,
  stripMentions,
} from "../../lib/coachingMentions";

/**
 * Coaching Tab — persistent coaching threads on a deal with @-mention tagging.
 *
 * v2 design (May 7, 2026):
 *   * Anyone can start, post, and resolve a thread (visibility unchanged).
 *   * Notifications + Team-line SMS now fan out to a thread's PARTICIPANT
 *     set rather than the hardcoded {starter, owner, split} trio. Starter,
 *     deal owner, and split RM are auto-added when a thread is created;
 *     anyone @-tagged in any message becomes a participant and stays
 *     subscribed for the rest of the thread.
 *   * Tagged users get a higher-signal `coaching_mention` notification
 *     (and SMS bracket) so the recipient sees the difference between
 *     "you were tagged" and "general thread activity".
 */

interface CoachingThread {
  id: string;
  deal_id: string;
  status: "open" | "resolved";
  started_by: string | null;
  started_by_name: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  messages: CoachingMessage[];
  participants: ThreadParticipant[];
}

interface CoachingMessage {
  id: string;
  thread_id: string;
  author_id: string | null;
  author_name: string;
  body: string;
  mentions: string[]; // uuid[] of explicitly @-tagged users in this message
  created_at: string;
}

interface ThreadParticipant {
  thread_id: string;
  user_id: string;
  source: "starter" | "owner" | "split" | "mention";
  added_at: string;
  full_name?: string;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function initials(name: string): string {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

interface Props {
  deal: Deal;
}

export default function CoachingTab({ deal }: Props) {
  const { member } = useAuth();
  const [threads, setThreads] = useState<CoachingThread[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [newThreadBody, setNewThreadBody] = useState("");
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  // Cache of team_roster {id → full_name} for rendering participant chips.
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const { data: threadData } = await supabase
      .from("coaching_threads")
      .select("*")
      .eq("deal_id", deal.id)
      .order("created_at", { ascending: false });

    if (!threadData) { setThreads([]); setLoading(false); return; }

    const threadIds = threadData.map((t) => t.id);
    let allMessages: CoachingMessage[] = [];
    let allParticipants: ThreadParticipant[] = [];
    if (threadIds.length > 0) {
      const [{ data: msgs }, { data: parts }] = await Promise.all([
        supabase
          .from("coaching_messages")
          .select("*")
          .in("thread_id", threadIds)
          .order("created_at", { ascending: true }),
        supabase
          .from("coaching_thread_participants")
          .select("thread_id, user_id, source, added_at")
          .in("thread_id", threadIds),
      ]);
      allMessages = (msgs ?? []) as CoachingMessage[];
      allParticipants = (parts ?? []) as ThreadParticipant[];
    }

    const enriched: CoachingThread[] = threadData.map((t) => ({
      ...t,
      messages: allMessages.filter((m) => m.thread_id === t.id),
      participants: allParticipants.filter((p) => p.thread_id === t.id),
    }));

    setThreads(enriched);
    setLoading(false);
  }, [deal.id]);

  useEffect(() => { load(); }, [load]);

  // Realtime: reload on changes to messages, threads, OR participants — the
  // participant table is what drives the chip strip and fan-out, so we want
  // it fresh.
  useEffect(() => {
    const channel = supabase
      .channel(`coaching-${deal.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "coaching_messages" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "coaching_threads", filter: `deal_id=eq.${deal.id}` }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "coaching_thread_participants" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [deal.id, load]);

  // Resolve participant uuids → display names for the chip strip. Only fetch
  // ids we don't already have cached.
  useEffect(() => {
    if (!threads) return;
    const need = new Set<string>();
    threads.forEach((t) => t.participants.forEach((p) => {
      if (!memberNames[p.user_id]) need.add(p.user_id);
    }));
    if (need.size === 0) return;
    supabase
      .from("team_roster")
      .select("id, full_name")
      .in("id", Array.from(need))
      .then(({ data }) => {
        if (!data) return;
        setMemberNames((prev) => {
          const next = { ...prev };
          for (const row of data) next[row.id] = row.full_name;
          return next;
        });
      });
  }, [threads, memberNames]);

  /**
   * Fan out notifications + Team-line SMS for one message in a thread.
   *
   * For each participant other than the author:
   *   - If the participant was @-tagged in THIS message → coaching_mention
   *     notification + "mention" SMS kind. Higher-signal for direct attention.
   *   - Otherwise → coaching_note (first message of a thread) or
   *     coaching_reply (subsequent messages).
   *
   * Participants list is the authoritative recipient set, so this works the
   * same whether the user was originally added as starter/owner/split or
   * @-tagged later.
   */
  function fanOutMessage(args: {
    thread_id: string;
    isFirstMessage: boolean;
    body: string;
    mentioned: string[];
    participants: ThreadParticipant[];
    senderId: string;
    senderName: string;
  }) {
    if (!member) return;
    const { thread_id, isFirstMessage, body, mentioned, participants, senderId, senderName } = args;
    const preview = stripMentions(body).slice(0, 200);
    const mentionedSet = new Set(mentioned);
    const seen = new Set<string>([senderId]); // never notify yourself

    for (const p of participants) {
      if (seen.has(p.user_id)) continue;
      seen.add(p.user_id);

      const wasMentioned = mentionedSet.has(p.user_id);
      const type = wasMentioned
        ? "coaching_mention"
        : (isFirstMessage ? "coaching_note" : "coaching_reply");
      const title = wasMentioned
        ? `${senderName} tagged you in a coaching thread`
        : (isFirstMessage
            ? `Coaching note from ${senderName}`
            : `${senderName} posted in coaching thread`);

      // Fire-and-forget. Notification insert + SMS in parallel.
      supabase.from("notifications").insert({
        user_id: p.user_id,
        type,
        title,
        body: preview,
        deal_id: deal.id,
        metadata: { thread_id, from: senderName, mentioned: wasMentioned },
      }).then(() => {});

      bridgeCoachingNote({
        thread_id,
        deal_id: deal.id,
        recipient_user_id: p.user_id,
        sender_name: senderName,
        sender_user_id: senderId,
        body,
        kind: wasMentioned ? "mention" : (isFirstMessage ? "note" : "reply"),
      });
    }
  }

  async function startThread() {
    if (!newThreadBody.trim() || !member || sending) return;
    setSending(true);
    try {
      const body = newThreadBody.trim();
      const mentioned = mentionUserIds(body);

      // 1. Create the thread row.
      const { data: thread, error: tErr } = await supabase
        .from("coaching_threads")
        .insert({
          deal_id: deal.id,
          started_by: member.id,
          started_by_name: member.full_name,
        })
        .select()
        .single();
      if (tErr || !thread) throw tErr || new Error("Thread creation failed");

      // 2. Insert the first message with explicit mentions[] for fast lookup.
      const { error: mErr } = await supabase.from("coaching_messages").insert({
        thread_id: thread.id,
        author_id: member.id,
        author_name: member.full_name,
        body,
        mentions: mentioned,
      });
      if (mErr) throw mErr;

      // 3. Build the initial participant set: starter + owner + split + everyone
      //    @-tagged. Dedupe by user_id; first source wins. We exclude any null
      //    uuids; we do NOT exclude the author here — including yourself in your
      //    own thread is harmless and the fan-out skips senders anyway.
      const candidates: { user_id: string; source: ThreadParticipant["source"] }[] = [];
      const seen = new Set<string>();
      const push = (uid: string | null | undefined, source: ThreadParticipant["source"]) => {
        if (!uid || seen.has(uid)) return;
        seen.add(uid);
        candidates.push({ user_id: uid, source });
      };
      push(member.id, "starter");
      push(deal.owner_id, "owner");
      push(deal.split_rm_id, "split");
      for (const uid of mentioned) push(uid, "mention");

      const partRows = candidates.map((c) => ({
        thread_id: thread.id,
        user_id: c.user_id,
        source: c.source,
        added_by: member.id,
      }));
      // Use upsert with onConflict so re-runs (e.g. realtime racing the insert)
      // don't fail. The (thread_id, user_id) PK handles dedup at the DB level.
      const { error: pErr } = await supabase
        .from("coaching_thread_participants")
        .upsert(partRows, { onConflict: "thread_id,user_id" });
      if (pErr) console.warn("[CoachingTab] participants upsert failed:", pErr);

      // 4. Fan out. Treat the freshly-built candidate list as the participants
      //    for this notification — the table will reflect the same state in
      //    the next reload but we don't want to round-trip before notifying.
      fanOutMessage({
        thread_id: thread.id,
        isFirstMessage: true,
        body,
        mentioned,
        participants: candidates.map((c) => ({
          thread_id: thread.id,
          user_id: c.user_id,
          source: c.source,
          added_at: new Date().toISOString(),
        })),
        senderId: member.id,
        senderName: member.full_name,
      });

      setNewThreadBody("");
      await load();
    } catch (err: unknown) {
      alert("Failed: " + formatSupabaseError(err));
    } finally {
      setSending(false);
    }
  }

  async function postReply(threadId: string) {
    const raw = replyText[threadId]?.trim();
    if (!raw || !member || sending) return;
    setSending(true);
    try {
      const mentioned = mentionUserIds(raw);

      // 1. Insert the reply message.
      const { error } = await supabase.from("coaching_messages").insert({
        thread_id: threadId,
        author_id: member.id,
        author_name: member.full_name,
        body: raw,
        mentions: mentioned,
      });
      if (error) throw error;

      // 2. Fetch the current participant list, then add any newly-mentioned
      //    users so they stay subscribed for future messages too.
      const { data: existingParts } = await supabase
        .from("coaching_thread_participants")
        .select("thread_id, user_id, source, added_at")
        .eq("thread_id", threadId);
      const existing = (existingParts ?? []) as ThreadParticipant[];
      const existingIds = new Set(existing.map((p) => p.user_id));

      const newParticipantRows = mentioned
        .filter((uid) => !existingIds.has(uid))
        .map((uid) => ({
          thread_id: threadId,
          user_id: uid,
          source: "mention" as const,
          added_by: member.id,
        }));
      if (newParticipantRows.length > 0) {
        const { error: pErr } = await supabase
          .from("coaching_thread_participants")
          .upsert(newParticipantRows, { onConflict: "thread_id,user_id" });
        if (pErr) console.warn("[CoachingTab] reply participants upsert failed:", pErr);
      }

      // 3. Combined recipient list = existing participants + newly added.
      const allParticipants: ThreadParticipant[] = [
        ...existing,
        ...newParticipantRows.map((r) => ({
          thread_id: r.thread_id,
          user_id: r.user_id,
          source: r.source,
          added_at: new Date().toISOString(),
        })),
      ];

      fanOutMessage({
        thread_id: threadId,
        isFirstMessage: false,
        body: raw,
        mentioned,
        participants: allParticipants,
        senderId: member.id,
        senderName: member.full_name,
      });

      setReplyText((prev) => ({ ...prev, [threadId]: "" }));
      await load();
    } catch (err: unknown) {
      alert("Reply failed: " + formatSupabaseError(err));
    } finally {
      setSending(false);
    }
  }

  async function resolveThread(threadId: string) {
    if (!member) return;
    await supabase.from("coaching_threads").update({
      status: "resolved",
      resolved_at: new Date().toISOString(),
      resolved_by: member.id,
    }).eq("id", threadId);
    await load();
  }

  async function reopenThread(threadId: string) {
    await supabase.from("coaching_threads").update({
      status: "open",
      resolved_at: null,
      resolved_by: null,
    }).eq("id", threadId);
    await load();
  }

  if (loading) return <div className="text-slate-500 py-8 text-center text-sm">Loading coaching threads…</div>;

  const openThreads = (threads ?? []).filter((t) => t.status === "open");
  const resolvedThreads = (threads ?? []).filter((t) => t.status === "resolved");

  return (
    <div className="space-y-4">
      {/* Start new thread */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-sm font-semibold text-slate-800 mb-2">Start a coaching thread</div>
        <div className="flex gap-2 items-start">
          <MentionInput
            value={newThreadBody}
            onChange={setNewThreadBody}
            onSubmit={startThread}
            placeholder="Ask a question, flag an issue, or @tag someone…"
            disabled={sending}
            excludeUserId={member?.id ?? null}
          />
          <button
            onClick={startThread}
            disabled={sending || !newThreadBody.trim()}
            className="px-4 py-2 rounded-lg bg-bardin-teal text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition shrink-0"
          >
            Post
          </button>
        </div>
        <div className="mt-1.5 text-[10px] text-slate-400">
          Type <span className="font-semibold text-slate-500">@</span> to tag a teammate. Tagged people get a notification, an SMS, and stay subscribed to this thread.
        </div>
      </div>

      {/* Open threads */}
      {openThreads.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Open threads</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700">{openThreads.length}</span>
          </div>
          {openThreads.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              memberNames={memberNames}
              currentMemberId={member?.id ?? null}
              replyText={replyText[thread.id] ?? ""}
              onReplyChange={(v) => setReplyText((p) => ({ ...p, [thread.id]: v }))}
              onPostReply={() => postReply(thread.id)}
              onResolve={() => resolveThread(thread.id)}
              onReopen={() => reopenThread(thread.id)}
              sending={sending}
            />
          ))}
        </div>
      )}

      {/* Resolved threads */}
      {resolvedThreads.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Resolved threads</div>
          {resolvedThreads.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              memberNames={memberNames}
              currentMemberId={member?.id ?? null}
              replyText={replyText[thread.id] ?? ""}
              onReplyChange={(v) => setReplyText((p) => ({ ...p, [thread.id]: v }))}
              onPostReply={() => postReply(thread.id)}
              onResolve={() => resolveThread(thread.id)}
              onReopen={() => reopenThread(thread.id)}
              sending={sending}
            />
          ))}
        </div>
      )}

      {(threads ?? []).length === 0 && (
        <div className="text-center py-8 text-slate-400 text-sm">No coaching threads yet. Start one above.</div>
      )}
    </div>
  );
}

function ThreadCard({
  thread, memberNames, currentMemberId, replyText, onReplyChange, onPostReply, onResolve, onReopen, sending,
}: {
  thread: CoachingThread;
  memberNames: Record<string, string>;
  currentMemberId: string | null;
  replyText: string;
  onReplyChange: (v: string) => void;
  onPostReply: () => void;
  onResolve: () => void;
  onReopen: () => void;
  sending: boolean;
}) {
  const isOpen = thread.status === "open";
  const colors = [
    "bg-bardin-teal text-white",
    "bg-emerald-500 text-white",
    "bg-indigo-500 text-white",
    "bg-amber-500 text-white",
    "bg-purple-500 text-white",
  ];

  // Sort participants: starter → owner → split → mentions (chronological).
  const sortedParticipants = useMemo(() => {
    const order: Record<ThreadParticipant["source"], number> = {
      starter: 0, owner: 1, split: 2, mention: 3,
    };
    return [...thread.participants].sort((a, b) => {
      const o = order[a.source] - order[b.source];
      return o !== 0 ? o : (a.added_at < b.added_at ? -1 : 1);
    });
  }, [thread.participants]);

  return (
    <div className={
      "bg-white border rounded-lg p-4 mb-2 " +
      (isOpen ? "border-slate-200" : "border-slate-100 opacity-70")
    }>
      {/* Participants chip strip — small, slate, just a who-can-see-this hint */}
      {sortedParticipants.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mb-2 pb-2 border-b border-slate-50">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mr-1">In thread</span>
          {sortedParticipants.map((p) => {
            const name = memberNames[p.user_id] ?? "loading…";
            const me = p.user_id === currentMemberId;
            return (
              <span
                key={p.user_id}
                className={
                  "text-[10px] font-medium px-1.5 py-0.5 rounded border " +
                  (me
                    ? "bg-teal-50 text-bardin-teal border-teal-100"
                    : p.source === "mention"
                    ? "bg-purple-50 text-purple-700 border-purple-100"
                    : "bg-slate-50 text-slate-600 border-slate-100")
                }
                title={`Added as ${p.source}`}
              >
                {name}
                {p.source === "mention" && <span className="ml-1 opacity-60">tagged</span>}
              </span>
            );
          })}
        </div>
      )}

      {/* Messages */}
      <div className="space-y-0">
        {thread.messages.map((msg, i) => {
          const colorClass = colors[msg.author_name.charCodeAt(0) % colors.length];
          return (
            <div key={msg.id} className="flex gap-3 py-2.5 border-b border-slate-50 last:border-b-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${colorClass}`}>
                {initials(msg.author_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold text-slate-800">{msg.author_name}</span>
                  <span className="text-[10px] text-slate-400">{timeAgo(msg.created_at)}</span>
                  {i === 0 && isOpen && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 ml-auto">Open</span>
                  )}
                  {i === 0 && !isOpen && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 ml-auto">Resolved</span>
                  )}
                </div>
                <div className="text-[12px] text-slate-700 mt-0.5 leading-relaxed whitespace-pre-wrap">
                  {renderMessageBody(msg.body)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply + actions */}
      {isOpen && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 items-start">
          <MentionInput
            value={replyText}
            onChange={onReplyChange}
            onSubmit={onPostReply}
            placeholder="Reply… (@tag to loop someone in)"
            disabled={sending}
            excludeUserId={currentMemberId}
            inputClassName="text-[12px]"
          />
          <button
            onClick={onPostReply}
            disabled={sending || !replyText.trim()}
            className="px-3 py-2 rounded-lg bg-bardin-teal text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 transition shrink-0"
          >
            Reply
          </button>
          <button
            onClick={onResolve}
            className="px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-[12px] font-semibold hover:bg-emerald-100 transition shrink-0"
          >
            Resolve
          </button>
        </div>
      )}

      {!isOpen && (
        <div className="flex justify-end mt-2 pt-2 border-t border-slate-100">
          <button
            onClick={onReopen}
            className="text-[11px] text-slate-400 hover:text-slate-600 transition"
          >
            Reopen thread
          </button>
        </div>
      )}
    </div>
  );
}
